import { PayloadAction } from "@reduxjs/toolkit";
import { editorInterface } from "../slice/editor";
import { ReqParamsProps, TextsData } from "../../@types/data";

/**
 * Retrieves a text property from `fetchedData` within the provided state object.
 *
 */
const getTextProp = (state, key) => state.fetchedData.dataTxtProp[key];

/**
 * Retrieves a specific text data from the fetched data stored in the state.
 *
 * @returns {string} - The text associated with the provided key from the fetched data.
 */
const getText = (state, key) => state.fetchedData.dataTxtCard[key];

/**
 * Updates a text property in the state object with given updates.
 *
 * This function retrieves a specific text property from the state using the provided key,
 * and merges the updates into that property if it exists.
 */
const updateTextProp = (state, key, updates) => {
  const textProp = getTextProp(state, key);
  if (textProp) {
    Object.assign(textProp, updates);
  }
};

/**
 * A function to update the properties of a text object within the state.
 *
 * This function retrieves the text object identified by the provided key from the given state,
 * and then applies the specified updates to that text object using Object.assign.
 * If no text object is found, no action is taken.
 */
const updateTextText = (state, key, updates) => {
  const text = getText(state, key)
  if (text) {
    Object.assign(text, updates);
  }
}

/**
 * Updates the font fixing settings in the state based on the given action payload.
 *
 * This function searches for an existing entry with the same key as provided
 * in the action payload. If found, it updates the existing entry. Otherwise,
 * it adds a new entry to the state.
 */
export const updateFixFontSize = (state: editorInterface, action: PayloadAction<{ key: string; fix: boolean }>) => {
  const existingIndex = state.fixFontSize.findIndex(item => item?.key === action.payload.key);

  if (existingIndex !== -1) {
    state.fixFontSize[existingIndex] = action.payload;
  } else {
    state.fixFontSize.push(action.payload);
  }
};

export const updateTooBigFontSizes = (state: editorInterface, action: PayloadAction<number>) => {
    state.limitFontSize= action.payload;
};

export const updateTextsData = (state: editorInterface, action: PayloadAction<TextsData>) => {
    const txtId = action.payload.index;
    if (state.textDatas.length > 0 && state.textDatas.find(item => item.layout !== action.payload.layout)) {
      state.textDatas = [];
    }
    
    const existingIndex = state.textDatas.findIndex(item => item.index === txtId);
    if (existingIndex !== -1) {
      state.textDatas[existingIndex] = action.payload;
    } else {
      state.textDatas.push(action.payload);
    }
    
    state.textDatas = [...state.textDatas].sort((a, b) => a.index - b.index);
};

export const updateTextGeneratorReqParams = (state: editorInterface, action: PayloadAction<ReqParamsProps[]>) => {
  state.textGeneratorReqParams = action.payload;
};

export const togglePlaceholder = (state: editorInterface, action: PayloadAction<number>) => {
  const existingIndex = state.placeholder.findIndex(item => item.index === action.payload);
  if (existingIndex !== -1) {
    state.placeholder[existingIndex] = {index: action.payload, show: false};
  } else {
    state.placeholder.push({index: action.payload, show: false});
  }
};

/**
 * Updates the text font family and font properties in the state object based on the provided action.
 */
export const updateTextFontFamily = (state, action) => {
  const { key, font, fontFamily } = action.payload;
  updateTextProp(state, key, { textFontFamily: fontFamily, textFont: font });
};

/**
 * Updates the text color properties in the state based on the provided action.
 */
export const updateTextColor = (state, action) => {
  const { key, value, type } = action.payload;
  updateTextProp(state, key, { textFontColor: value, textFontColorType: type });
};

/**
 * Updates the text size of a given text property.
 */
export const updateTextSize = (state, action) => {
  const { key, value } = action.payload;
  updateTextProp(state, key, { textFontSize: value });
};

/**
 * Updates the vertical alignment of a text element within the application state.
 *
 * This function takes in the current state and an action object that contains
 * the key of the text element to update and the new vertical alignment value.
 * It uses the 'updateTextProp' function to apply the vertical alignment update
 * to the text element identified by the provided key.
 */
export const updateVAlign = (state, action) => {
  const { key, vAlign } = action.payload;
  updateTextProp(state, key, { textVAlign: vAlign });
};

/**
 * Updates the horizontal alignment (hAlign) property for a given text element within the state.
 *
 * The function extracts the key and hAlign from the action payload and uses the updateTextProp function
 * to set the new horizontal alignment for the specified text element within the state.
 */
export const updateHAlign = (state, action) => {
  const { key, hAlign } = action.payload;
  updateTextProp(state, key, { textAlign: hAlign });
};

/**
 * Updates a specific field in the state with a new value provided in the action payload.
 */
export const updateText = (state, action) => {
  const { key, value } = action.payload;

  updateTextText(state, key, { value: value })
}